import React from 'react';

import { useFeatureFlag } from '@betterment/shared-components';
import {
  Box,
  CONTENT_MAX_WIDTH,
  Flex,
  FlexChildProps,
  GoalIcon,
  Heading,
  SectionLayout,
  SiteLayout,
  TextProps,
  get as themeGet,
} from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { space } from 'styled-system';

import { useAccount } from './AccountProvider';
import Balance from './Balance';
import { useCustomer } from './CustomerProvider';

import { trackClick } from '../../features/track';
import { ThemedImage } from '../../Shared/components-v2/themed-image/ThemedImage';
import { urlBuilder } from '../../Shared/urlBuilder';
import { useUser } from '../contexts/UserProvider';

interface Props {
  title: string;
  children:
    | React.ReactNode
    | [React.ReactNode, React.ReactNode]
    | [React.ReactNode, React.ReactNode, React.ReactNode]
    | null;
}

const ActiveLink = styled(NavLink)<Omit<TextProps, 'color'>>`
  && {
    font-size: ${themeGet('fontSizes.2')};
    color: ${themeGet('colors.bmtGrey50')};
  }
  &&.active {
    border-bottom: 2px solid ${themeGet('colors.bmtGrey90')};
    color: ${themeGet('colors.bmtGrey90')};
  }
  &:hover,
  &:active {
    text-decoration: none;
    color: ${themeGet('colors.bmtGrey90')};
    outline-color: transparent;
  }
  &:focus {
    outline-color: 'blue';
  }
  ${space}
`;

const FlexFullViewportWidth = styled(Flex)`
  width: 100vw;
`;
const SiteLayoutStyled = styled(SiteLayout)`
  height: auto;
`;

interface SlotProps {
  order?: FlexChildProps['order'];
}

export const Slot: React.FC<React.PropsWithChildren<SlotProps>> = ({ children }) => <>{children}</>;
export const Banner: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => <>{children}</>;

interface LayoutComponent<P = {}> extends React.FC<P> {
  Slot: React.FC<React.PropsWithChildren<SlotProps>>;
  Banner: React.FC<React.PropsWithChildren<unknown>>;
}

const CheckingLayout: LayoutComponent<Props> = ({ title, children }) => {
  const { account } = useAccount();
  const { user } = useUser();
  const { customer } = useCustomer();
  const { enabled: isEnvelopesExperienceEnabled } = useFeatureFlag({
    splitName: 'retail.envelopes_experience_q1_2025_enabled',
    context: 'CheckingSummary',
  });
  const icon = { light: account.icon.lightUrl, dark: account.icon.darkUrl };

  const hasMultipleBettermentAccounts = !user?.isCheckingOnly || customer.accounts.length > 1;
  const [item1, item2, item3] = React.Children.toArray(children) as React.ReactElement[];

  let [slot1, slot2]: React.ReactElement<SlotProps>[] = [];
  let banner: React.ReactElement | false = false;

  if (item1.type === Banner) {
    banner = item1;
    slot1 = item2 as React.ReactElement<SlotProps>;
    slot2 = item3 as React.ReactElement<SlotProps>;
  } else {
    slot1 = item1 as React.ReactElement<SlotProps>;
    slot2 = item2 as React.ReactElement<SlotProps>;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <SiteLayoutStyled>
        <SiteLayout.Header mb={[6, 6, 0]}>
          {banner && banner}
          <SectionLayout bg="white" px="0">
            <FlexFullViewportWidth justifyContent="center" pt={[8, 8, 9]}>
              <Box width="100%">
                <Box px={[6, 6, 9]}>
                  <Box maxWidth={CONTENT_MAX_WIDTH} mx="auto" width="100%">
                    <Flex flexDirection="column">
                      <Flex alignItems="center">
                        <Box width={['4rem', '4rem', '4.8rem']} height={['4rem', '4rem', '4.8rem']}>
                          {isEnvelopesExperienceEnabled ? (
                            <ThemedImage
                              themedAsset={icon}
                              alt="checking"
                              css={{
                                width: { base: 'size.600' },
                                height: { base: 'size.600' },
                              }}
                            />
                          ) : (
                            <GoalIcon name="card" size={10} mr={3} />
                          )}
                        </Box>
                        <Heading variant="primary" pl={[2, 2, 2, 6]} mb="0">
                          {account.name}
                        </Heading>
                      </Flex>
                    </Flex>
                  </Box>
                </Box>
                <Box display={['block', 'block', 'none']}>
                  <Box borderBottom="1px solid" borderColor="bmtGrey20" bg="white" width="100%">
                    <Box px="6">
                      <Flex alignItems="baseline" justifyContent="flex-start" pt="9">
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          mr="9"
                          to={urlBuilder.getAppCheckingSummary({ accountId: account.id })}
                          end={true}
                          onClick={() => trackClick({ name: 'CheckingOverview' })}
                        >
                          Overview
                        </ActiveLink>
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          to={urlBuilder.getAppCheckingAccountTransactions({ accountId: account.id })}
                          onClick={() => trackClick({ name: 'CheckingTransactions' })}
                        >
                          Transactions
                        </ActiveLink>
                      </Flex>
                    </Box>
                  </Box>
                  <Box px="6">
                    <Balance {...account} hasMultipleBettermentAccounts={hasMultipleBettermentAccounts} />
                  </Box>
                </Box>

                <Box display={['none', 'none', 'block']}>
                  <Box borderBottom="1px solid" borderColor="bmtGrey20" bg="white" width="100%">
                    <Box px="9">
                      <Box maxWidth={CONTENT_MAX_WIDTH} mx="auto" width="100%">
                        <Balance {...account} hasMultipleBettermentAccounts={hasMultipleBettermentAccounts} />
                      </Box>
                    </Box>
                  </Box>
                  <Box px="9">
                    <Box maxWidth={CONTENT_MAX_WIDTH} mx="auto" width="100%">
                      <Flex alignItems="baseline" justifyContent="flex-start" pt="6">
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          mr="9"
                          to={urlBuilder.getAppCheckingSummary({ accountId: account.id })}
                          end={true}
                          onClick={() => trackClick({ name: 'CheckingOverview' })}
                        >
                          Overview
                        </ActiveLink>
                        <ActiveLink
                          fontSize="2"
                          pb="6"
                          to={urlBuilder.getAppCheckingAccountTransactions({ accountId: account.id })}
                          onClick={() => trackClick({ name: 'CheckingTransactions' })}
                        >
                          Transactions
                        </ActiveLink>
                      </Flex>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </FlexFullViewportWidth>
          </SectionLayout>
        </SiteLayout.Header>
        <SiteLayout.Main>
          <Flex justifyContent="center">
            <Box
              maxWidth={CONTENT_MAX_WIDTH}
              mx={[0, 0, 9]}
              width="100%"
              borderTop={['1px solid', null, 'none']}
              borderColor="bmtGrey20"
            >
              <Flex flexWrap={['wrap', null, 'nowrap']}>
                <Flex.Item order={slot1.props.order} flex="1 1 auto" mb={[3, 3, 0]}>
                  {slot1}
                </Flex.Item>
                {slot2 && (
                  <Flex.Item
                    order={slot2.props.order}
                    flex={['1 1 auto', null, '0 0 33.33%']}
                    pl={[0, 0, 9]}
                    pt={[0, 0, 9]}
                  >
                    {slot2}
                  </Flex.Item>
                )}
              </Flex>
            </Box>
          </Flex>
        </SiteLayout.Main>
      </SiteLayoutStyled>
    </>
  );
};

CheckingLayout.Slot = Slot;
CheckingLayout.Banner = Banner;

export default CheckingLayout;
